import { useEffect } from "react";

const COMPONENT_PRIORITY_MAX = process.env.COMPONENT_PRIORITY_MAX || 5;

export const useTestingSimulation = ({ setComponents, componentQueue, setErrors }) => {
  useEffect(() => {
    // Only set up testing simulation in dev mode
    if (!process.env.REACT_APP_DEV_MODE) {
      return;
    }

    console.log("🧪 Setting up testing simulation hooks");

    const handleComponentFinished = (uid) => {
      const componentToFinish = componentQueue.current.find((comp) => comp.uid === uid);

      const updateComponents = () => {
        setComponents((prev) => {
          // Remove the finished component
          const filteredComponents = prev.filter((comp) => comp.uid !== uid);

          // If there are remaining components, show the one with highest priority
          let returnData = [];
          if (filteredComponents.length > 0)
            returnData = filteredComponents.sort((a, b) => b.priority - a.priority);
          componentQueue.current = returnData;
          return returnData;
        });
      };

      if (componentToFinish?.data?.close_delay) {
        setTimeout(() => {
          updateComponents();
        }, componentToFinish.data.close_delay);
      } else updateComponents();
    };

    const handleSimulateMessage = (event) => {
      console.log("🧪 Simulating RCL message:", event.detail);

      // Modified version of hadnleRclnodemessage for testing (without socket operations)
      const data = event.detail;

      if (data?.reset) {
        setComponents([]);
        componentQueue.current = [];
        setErrors({});
        return;
      }

      // Validate priority
      let priority = data?.priority;
      if (!priority) {
        if (data?.component === "Toast" || data?.component === "Modal") {
          priority = 4;
        } else priority = 1;
      }
      if (typeof priority !== "number" || priority < 0 || priority > COMPONENT_PRIORITY_MAX) {
        const errorMsg = `Invalid priority value: ${priority}. Priority must be between 1 and ${COMPONENT_PRIORITY_MAX}`;
        console.error(errorMsg);
        setErrors({ priority: errorMsg });
        return;
      }

      const finishComponent = (id) => {
        // For testing, just log instead of emitting to socket
        console.log("🧪 Testing: Component finished:", id);
      };

      setComponents((prev) => {
        // Check if this is an update to existing component (has component_id)
        const isUpdate = data?.component_id;
        const existingComponentIndex = isUpdate
          ? prev.findIndex((comp) => comp.component_id === data.component_id)
          : -1;

        if (isUpdate && existingComponentIndex !== -1) {
          // UPDATE EXISTING COMPONENT
          const existingComponent = prev[existingComponentIndex];

          // Merge data (partial update)
          const parsedNewData =
            typeof data?.data === "string" ? JSON.parse(data?.data) : data?.data;
          const mergedData = { ...existingComponent.data, ...parsedNewData };

          const updatedComponent = {
            ...existingComponent,
            data: mergedData,
            priority: priority, // Allow priority updates
          };

          console.log(`✅ Updated existing component with ID: ${data.component_id}`);

          // Replace the existing component
          const newComponents = [...prev];
          newComponents[existingComponentIndex] = updatedComponent;

          // Re-sort by priority
          const returnData = newComponents.sort((a, b) => b.priority - a.priority);
          componentQueue.current = returnData;
          return returnData;
        }

        // CREATE NEW COMPONENT (either no component_id provided or component doesn't exist)

        // Check if there's an active higher priority component
        const highestPriorityComponent = prev[0];

        if (highestPriorityComponent && priority < highestPriorityComponent.priority) {
          // Reject lower priority component if higher priority is active
          console.log("🚀 ~ setComponents ~ Rejecting lower priority component");
          componentQueue.current = prev;
          return prev;
        }

        const newComponent = {
          component: data?.component,
          data: typeof data?.data === "string" ? JSON.parse(data?.data) : data?.data,
          uid: data?.uid,
          component_id: data?.component_id, // Store component_id for future updates
          priority: priority,
        };

        if (data?.override) {
          // Remove components with same and lower priority
          const filteredComponents = prev.filter((comp) => comp.priority > newComponent.priority);

          // Finish components with same or lower priority
          const componentsToRemove = prev.filter((comp) => comp.priority <= newComponent.priority);
          componentsToRemove.forEach((comp) => {
            finishComponent(comp.uid);
          });

          const returnData = [newComponent, ...filteredComponents];
          componentQueue.current = returnData;
          return returnData;
        } else {
          // Remove components with same priority
          const filteredComponents = prev.filter((comp) => comp.priority !== newComponent.priority);

          // Finish component with same priority
          const componentToFinish = prev.find((comp) => comp.priority === newComponent.priority);
          if (componentToFinish) finishComponent(componentToFinish.uid);

          const returnData = [...filteredComponents, newComponent].sort(
            (a, b) => b.priority - a.priority,
          );
          componentQueue.current = returnData;
          return returnData;
        }
      });
    };

    const handleSimulateFinish = (event) => {
      console.log("🧪 Simulating component finish:", event.detail);
      handleComponentFinished(event.detail.uid);
    };

    // Add event listeners
    window.addEventListener("simulate-rclnode-message", handleSimulateMessage);
    window.addEventListener("simulate-component-finish", handleSimulateFinish);

    // Cleanup function
    return () => {
      console.log("🧪 Cleaning up testing simulation hooks");
      window.removeEventListener("simulate-rclnode-message", handleSimulateMessage);
      window.removeEventListener("simulate-component-finish", handleSimulateFinish);
    };
  }, [setComponents, componentQueue, setErrors]);
};
